<template>

    <div class="border rounded">
        <div class="divide-y" v-if="tags.length">
            <div v-for="(tag, index) in tags">
                <div class="flex justify-end items-center gap-x-4 p-4" v-if="tag.showTagEditForm">
                    <text-field
                        class="w-full"
                        v-model="tag.value"
                        v-on:keyup.enter="updateTag(tag)"
                        :class="{'bg-red-100 border border-red-400 text-red-700': errors.editingTag[tag.key]}"
                    ></text-field>
                    <span v-if="errors.editingTag[tag.key]">{{ errors.editingTag[tag.key] }}</span>

                    <btn class="shrink-0" type="secondary" @click="updateTag(tag)" :loading="loading.editingTag">Save</btn>
                    <btn class="shrink-0" type="secondary" @click="tag.showTagEditForm = false">Cancel</btn>
                </div>
                <div class="flex justify-end items-center gap-x-4 p-4" v-if="!tag.showTagEditForm">
                    <div class="w-full mr-auto text-sm">{{ tag.value }}</div>
                    <div class="flex items-center gap-x-2 shrink-0">
                        <button type="button" class="focus:outline-none" @click="tag.showTagEditForm = !tag.showTagEditForm">
                            <icon name="settings"></icon>
                        </button>
                        <modal size="small" title="Please confirm" name="delete_tag_modal">
                            <template #toggle>
                                <button type="button" class="focus:outline-none">
                                    <icon name="delete"></icon>
                                </button>
                            </template>
                            <template #body>
                                <div class="text-sm text-gray-500">
                                    Are you sure you want to delete the partner tag <strong>{{ tag.value }}</strong>? <br />
                                    This action will remove it from all partners and associated commission settings.
                                </div>
                            </template>
                            <template #footer>
                                <btn @click="deleteTag(tag)" :loading="loading.deletingTag">Yes, delete</btn>
                            </template>
                        </modal>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="w-full p-6 text-sm text-gray-500 text-center">
            No partner tags available. Create a new one.
        </div>
    </div>

    <div class="flex justify-end" v-if="!showNewPartnerTagForm">
        <btn @click="showNewPartnerTagForm = !showNewPartnerTagForm">Create new tag</btn>
    </div>
    <div class="space-y-4" v-if="showNewPartnerTagForm">
        <alert type="error" v-if="errors.addingTag">{{ errors.addingTag }}</alert>
        <div class="flex justify-end gap-x-2">
            <text-field
                class="w-full mr-auto"
                placeholder="Enter a name for the tag, e.g. Premium"
                v-model="newTagInput"
                v-on:keyup.enter="addTag"
                :class="{'border-red-400': errors.addingTag}"
            ></text-field>
            <btn type="secondary" class="shrink-0" @click="addTag" :loading="loading.addingTag">Add</btn>
            <btn type="secondary" class="shrink-0" @click="showNewPartnerTagForm = !showNewPartnerTagForm">Cancel</btn>
        </div>
    </div>

</template>

<script setup>

import {ref, inject} from 'vue';
import TextField from "../../../../elements/forms/TextField.vue";
import Btn from "../../../../elements/Btn.vue";
import Alert from "../../../../elements/Alert";

const emit = defineEmits(['update:programPartnerTags'])
const emitter = inject('emitter');

const props = defineProps({
    program: {
        type: Number,
        required: true
    },
    programPartnerTags: {
        type: Array,
        default: []
    },
});

const showNewPartnerTagForm = ref(false);
const tags = ref(props.programPartnerTags);
const newTagInput = ref('');

const loading = ref({
    addingTag: false,
    editingTag: false,
    deletingTag: false,
});

const errors = ref({
    addingTag: null,
    editingTag: {},
    deletingTag: null,
});

const addTag = () => {

    if (loading.value.addingTag) {
        return;
    }

    errors.value.addingTag = null;
    loading.value.addingTag = true;

    axios.post(route('app_int.program_partner_tags.add', {program: props.program}), {
        tag: newTagInput.value,
    }).then(response => {
        const status = parseInt(response.data.status ?? -1);

        if (status === 1 && response.data.program_partner_tags) {
            tags.value = response.data.program_partner_tags;
            emit('update:programPartnerTags', tags);
            showNewPartnerTagForm.value = false;
            newTagInput.value = null;
        }

        if (status === 0 && response.data.errors[0]) {
            errors.value.addingTag = response.data.errors[0];
        }

    }).finally(() => {
        loading.value.addingTag = false;
    });
}
const updateTag = (tag) => {

    if (loading.value.editingTag) {
        return;
    }

    loading.value.editingTag = true;
    errors.value.editingTag[tag.key] = null;

    axios.post(route('app_int.program_partner_tags.edit', {program: props.program}), {
        tag: tag.key,
        value: tag.value,
    }).then(response => {
        const status = parseInt(response.data.status ?? -1);
        if (status === 1 && response.data.program_partner_tags) {
            tags.value = response.data.program_partner_tags;
            emit('update:programPartnerTags', tags);
        }

        if (status === 0 && response.data.errors[0]) {
            errors.value.editingTag[tag.key] = response.data.errors[0];
        }
    }).finally(() => {
        loading.value.editingTag = false;
    });
}

const deleteTag = (tag) => {
    if (loading.value.deletingTag) {
        return;
    }
    loading.value.deletingTag = true;

    axios.post(route('app_int.program_partner_tags.delete', {program: props.program}), {
        tag: tag.key,
    }).then(response => {
        const status = parseInt(response.data.status || -1);
        if (status === 1) {
            const index = tags.value.findIndex(item => item.key === tag.key);
            if (index !== -1) {
                tags.value.splice(index, 1);
                emit('update:programPartnerTags', tags);
            }

            emitter.emit('modal-off', 'delete_tag_modal')
        }
    }).finally(() => {
        loading.value.deletingTag = false;
    });
}

</script>
<style scoped></style>
